/**
 * Contract objects as first-class citizens
 *
 * Following the same patterns as the simulator:
 * - Direct function calls: contract.functionName()
 * - Write operations: contract.connect(signer).functionName()
 * - Event listening: contract.on('EventName', handler)
 */

import { Contract, JsonRpcProvider, BrowserProvider, Signer, InterfaceAbi, ethers } from 'ethers';
import { RaffleMiningABI } from '@/abi/RaffleMiningABI';
import { RaffleMiningViewsABI } from '@/abi/RaffleMiningViewsABI';
import { WelephantSwapABI } from '@/abi/WelephantSwapABI';
import { WelephantRewardsABI } from '@/abi/WelephantRewardsABI';
import { AutoPlayDepositABI } from '@/abi/AutoPlayDepositABI';
import { PayDayInfoListABI } from '@/abi/PayDayInfoListABI';

// Re-export ABIs for direct use
export { RaffleMiningABI as RAFFLE_MINING_ABI };
export { RaffleMiningViewsABI as RAFFLE_MINING_VIEWS_ABI };

// RoundPhase enum mapping (mirrors Solidity enum RoundPhase in RaffleMiningTypes.sol)
export const ROUND_PHASE_NAMES = [
  'waiting_for_players',
  'plays_open',
  'needs_closing',
  'cooldown',
  'ready_to_finalize',
  'finalized',
] as const;

// Chain ID for game contracts (1337 = hardhat, 56 = BNB mainnet, 97 = BNB testnet)
export const GAME_CHAIN_ID = parseInt(import.meta.env.VITE_CHAIN_ID || '1337', 10);

// Contract addresses from environment (no fallbacks - must be configured)
export const CONTRACT_ADDRESS = import.meta.env.VITE_CONTRACT_ADDRESS;
export const VIEWS_CONTRACT_ADDRESS = import.meta.env.VITE_VIEWS_CONTRACT_ADDRESS;
export const SWAPPER_ADDRESS = import.meta.env.VITE_SWAPPER_ADDRESS;
export const STAKING_VAULT_ADDRESS = import.meta.env.VITE_STAKING_VAULT_ADDRESS;
export const WELEPHANT_ADDRESS = import.meta.env.VITE_WELEPHANT_ADDRESS;
export const WELEPHANT_REWARDS_ADDRESS = import.meta.env.VITE_WELEPHANT_REWARDS_ADDRESS;
export const AUTO_PLAY_DEPOSIT_ADDRESS = import.meta.env.VITE_AUTO_PLAY_DEPOSIT_ADDRESS;
export const STREAM_VAULT_ADDRESS = import.meta.env.VITE_STREAM_VAULT_ADDRESS;
export const BNB_WELEPHANT_SWAP_ADDRESS = import.meta.env.VITE_BNB_WELEPHANT_SWAP_ADDRESS;
export const VANITY_REGISTRY_ADDRESS = import.meta.env.VITE_VANITY_REGISTRY_ADDRESS;
export const MULTICALL3_ADDRESS = import.meta.env.VITE_MULTICALL3_ADDRESS;
export const PAYDAY_INFO_LIST_ADDRESS = import.meta.env.VITE_PAYDAY_INFO_LIST_ADDRESS;
export const MULTISEND_ADDRESS = import.meta.env.VITE_MULTISEND_ADDRESS;
export const BNB_DIRECT_TRANSFER_ADDRESS = import.meta.env.VITE_BNB_DIRECT_TRANSFER_ADDRESS;

// Gas limit for all UI transactions (default 10M, configurable via VITE_GAS_LIMIT env var)
export const GAS_LIMIT = BigInt(import.meta.env.VITE_GAS_LIMIT || '10000000');

// Import chain-based Multicall3 addresses for production chains
import { getMulticall3Address } from './chains';

/**
 * Get Multicall3 address for a given chain ID
 * Uses canonical address for known chains (Base, BNB), falls back to env variable for local dev
 */
export function getMulticall3AddressForChain(chainId: number): string {
  // Try canonical address first (for Base, BNB Chain)
  const canonicalAddress = getMulticall3Address(chainId);
  if (canonicalAddress) {
    return canonicalAddress;
  }
  // Fall back to env variable (for local Hardhat)
  return MULTICALL3_ADDRESS;
}

// Type definitions for contract return types
export interface RoundInfo {
  startTime: bigint;
  endTime: bigint;
  totalPot: bigint;
  finalized: boolean;
  winningSquare: bigint;
  hasMinPlayers: boolean;
  participantCount: bigint;
}

export interface SquareInfo {
  total: bigint;
  playerCount: number;
}

export interface AutoPlayInfo {
  amountPerSquare: bigint;
  numberOfSquares: bigint;
  initialRounds: bigint;
  roundsPlayed: bigint;
  active: boolean;
}

export interface PlayerBalances {
  ethBalance: bigint;
  welephantBalance: bigint;
}

export interface PlayerStats {
  ethSpent: bigint;
  roundsParticipated: bigint;
  roundsWon: bigint;
  ethWon: bigint;
  welephantWon: bigint;
}

export interface PlayerAutoPlaySettings {
  useManualSquares: boolean;
  squaresBitmap: bigint;
  loopEthRewards: boolean;
  autoStakeWelephant: boolean;
}

export interface PlayerRoundInfo {
  roundId: bigint;
  totalPlay: bigint;
  ethWon: bigint;
  welephantWon: bigint;
  won: boolean;
  squareBitmask: bigint;
  amountPerSquare: bigint;
}

export interface PlayerRoundPerformance {
  participated: boolean;
  totalPlay: bigint;
  ethWon: bigint;
  welephantWon: bigint;
  won: boolean;
  squareBitmask: bigint;
}

export interface RoundSummary {
  roundId: bigint;
  startTime: bigint;
  endTime: bigint;
  startBlock: bigint;
  endBlock: bigint;
  closingBlock: bigint;
  totalPot: bigint;
  gameFee: bigint;
  welephantBoughtBack: bigint;
  paydayAmount: bigint;
  winnerPayout: bigint;
  welephantDistributed: bigint;
  winningSquare: bigint;
  totalPlayers: bigint;
  totalWinners: bigint;
  isPayday: boolean;
  isSingleWinner: boolean;
  finalized: boolean;
  singleWinner: string;
}

export interface RoundVerification {
  accumulatedSeed: string;
  finalRandomSeed: string;
  blockhash1: string;
  blockhash2: string;
  blockhash3: string;
  blockTimestamp: bigint;
  prevrandao: bigint;
  finalizationBlock: bigint;
  numSquares: bigint;
  paydayChance: bigint;
  singleWinnerChance: bigint;
  computedWinningSquare: bigint;
  storedWinningSquare: bigint;
  computedIsPayday: boolean;
  storedIsPayday: boolean;
  computedIsSingleWinner: boolean;
  storedIsSingleWinner: boolean;
  isValid: boolean;
}

export interface PlayerDetailedStats {
  totalRounds: bigint;
  totalPlay: bigint;
  totalWon: bigint;
  totalWelephantWon: bigint;
  averagePlay: bigint;
}

export interface PaydayInfo {
  roundId: bigint;
  blockNumber: bigint;
  timestamp: bigint;
  amount: bigint;
  numPlayers: bigint;
}

/**
 * Typed wrapper for RaffleMining contract
 *
 * Usage:
 * - Read: await raffleMining.numSquares()
 * - Write: await raffleMining.connect(signer).play(players, squareBitmasks, amountsPerSquare, { value })
 *          (each bitmask encodes selected squares: bit N-1 = square N, e.g. 1n << 4n = square 5)
 */
export interface RaffleMiningContract extends Contract {
  // Read functions
  numSquares(): Promise<bigint>;
  paused(): Promise<boolean>;
  paydayFund(): Promise<bigint>;
  gasTaxBasisPoints(): Promise<bigint>;
  getRoundPhase(roundId: bigint): Promise<[bigint, bigint]>;
  gameHistory(): Promise<string>;

  // Player data
  autoPlays(address: string): Promise<{ active: boolean; initialRounds: bigint; roundsPlayed: bigint }>;
  autoPlayRegistry(): Promise<string>;
  getPlayerAutoPlaySettings(address: string): Promise<[boolean, bigint, boolean, boolean]>;

  // Gas calculation
  calculateGasCharge(totalAmount: bigint): Promise<bigint>;

  // Round verification
  verifyRoundRandomness(roundId: bigint): Promise<[
    string, string, string, string, string,  // bytes32 fields (0-4)
    bigint, bigint, bigint, bigint, bigint,  // uint256 fields (5-9)
    bigint, bigint, bigint,                  // uint256 fields (10-12)
    boolean, boolean, boolean, boolean,      // bool flags (13-16)
    boolean                                  // isValid (17)
  ]>;

  // Write functions (call with .connect(signer))
  play(players: string[], squareBitmasks: bigint[], amountsPerSquare: bigint[], overrides?: { value?: bigint; gasLimit?: bigint; gasPrice?: bigint; nonce?: number }): Promise<ethers.ContractTransactionResponse>;
  registerAutoPlay(player: string, amountPerSquare: bigint, numberOfSquares: bigint, numOfRounds: bigint, loopEthRewards: boolean, autoStakeWelephant: boolean, useManualSquares: boolean, squares: bigint[], overrides?: { value?: bigint; gasLimit?: bigint; gasPrice?: bigint; nonce?: number }): Promise<ethers.ContractTransactionResponse>;
  cancelAutoPlay(overrides?: { gasLimit?: bigint; gasPrice?: bigint; nonce?: number }): Promise<ethers.ContractTransactionResponse>;
  canCancelAutoPlay(player: string): Promise<[boolean, boolean, bigint]>;

  // Connect to signer for write operations
  connect(signer: Signer): RaffleMiningContract;
}

/**
 * Typed wrapper for RaffleMiningViews contract (read-only)
 */
export interface RaffleMiningViewsContract extends Contract {
  // Round info
  getRoundInfo(roundId: bigint): Promise<[bigint, bigint, bigint, boolean, bigint, boolean, bigint]>;
  getRoundSummaryById(roundId: bigint): Promise<RoundSummary>;
  getRecentRounds(count: bigint): Promise<RoundSummary[]>;
  getRoundSummaries(offset: bigint, limit: bigint): Promise<RoundSummary[]>;
  getTotalRounds(): Promise<bigint>;
  getSquareInfo(roundId: bigint, square: bigint): Promise<[bigint]>;
  getAllSquaresInfo(roundId: bigint): Promise<[bigint[]]>;
  getPlayerSquareData(roundId: bigint, player: string): Promise<[bigint, bigint]>;
  getPlayerSquareAmounts(roundId: bigint, player: string): Promise<bigint[]>;
  getRoundParticipants(roundId: bigint, offset: bigint, limit: bigint): Promise<string[]>;
  getRoundParticipantCount(roundId: bigint): Promise<bigint>;

  // Player info
  getAutoPlayInfo(address: string): Promise<unknown[]>;
  getActiveAutoPlayCount(): Promise<bigint>;
  getPlayerDetailedStats(address: string): Promise<[bigint, bigint, bigint, bigint, bigint]>;
  getPlayerTotalRounds(address: string): Promise<bigint>;
  getPlayerRoundPerformance(address: string, roundId: bigint): Promise<[boolean, bigint, bigint, bigint, boolean, bigint, bigint]>;
  getPlayersRoundPerformance(addresses: string[], roundId: bigint): Promise<PlayerRoundPerformance[]>;

  // Paginated functions (safe for large datasets)
  getPlayerRoundHistoryPaginated(address: string, offset: bigint, limit: bigint): Promise<[PlayerRoundInfo[], bigint]>;
  getPlayerRoundIdsPaginated(address: string, offset: bigint, limit: bigint): Promise<[bigint[], bigint]>;
  getRoundParticipantsPaginated(roundId: bigint, offset: bigint, limit: bigint): Promise<[string[], bigint]>;
}

/**
 * Typed wrapper for WelephantSwap contract
 *
 * Usage:
 * - Read: await swapContract.pathEthForCore(amount)
 * - Write: await swapContract.connect(signer).swapExactETHForWrapped(minOut, to, { value })
 */
export interface WelephantSwapContract extends Contract {
  // Swap functions (call with .connect(signer))
  swapExactETHForWrapped(
    amountOutMin: bigint,
    to: string,
    overrides?: { value?: bigint; gasLimit?: bigint; nonce?: number }
  ): Promise<ethers.ContractTransactionResponse>;

  swapExactWrappedForETH(
    wrappedAmount: bigint,
    amountOutMin: bigint,
    to: string,
    overrides?: { gasLimit?: bigint; nonce?: number }
  ): Promise<ethers.ContractTransactionResponse>;

  // View functions
  pathEthForCore(wethAmount: bigint): Promise<[string[], bigint, bigint, bigint]>;
  pathCoreForEth(coreAmount: bigint): Promise<[string[], bigint, bigint, bigint]>;
  estimateMinETHForWrapped(wrappedAmount: bigint, slippage: bigint): Promise<bigint>;
  estimateMinWrappedForETH(ethAmount: bigint, slippage: bigint): Promise<bigint>;
  estimateUsdForETH(wethAmount: bigint): Promise<bigint>;

  // Simple price convenience functions
  getWelephantPriceInETH(): Promise<bigint>;
  getWelephantPriceInUSD(): Promise<bigint>;

  // Connect to signer for write operations
  connect(signer: Signer): WelephantSwapContract;
}

/**
 * Typed wrapper for WelephantRewards contract
 *
 * Usage:
 * - Read: await welephantRewards.getBalances(address)
 * - Write: await welephantRewards.connect(signer).claimRewards()
 */
export interface WelephantRewardsContract extends Contract {
  // View functions
  getBalances(address: string): Promise<[bigint, bigint]>;
  ethBalances(address: string): Promise<bigint>;
  welephantBalances(address: string): Promise<bigint>;

  // Write functions (call with .connect(signer))
  claimRewards(overrides?: { gasLimit?: bigint; nonce?: number }): Promise<ethers.ContractTransactionResponse>;

  // Connect to signer for write operations
  connect(signer: Signer): WelephantRewardsContract;
}

/**
 * Create a RaffleMining contract instance
 */
export function createRaffleMiningContract(
  provider: JsonRpcProvider | BrowserProvider
): RaffleMiningContract {
  return new Contract(CONTRACT_ADDRESS, RaffleMiningABI as InterfaceAbi, provider) as RaffleMiningContract;
}

/**
 * Create a RaffleMiningViews contract instance (read-only)
 */
export function createViewsContract(
  provider: JsonRpcProvider | BrowserProvider
): RaffleMiningViewsContract {
  return new Contract(VIEWS_CONTRACT_ADDRESS, RaffleMiningViewsABI as InterfaceAbi, provider) as RaffleMiningViewsContract;
}

/**
 * Create a WelephantSwap contract instance
 */
export function createSwapContract(
  provider: JsonRpcProvider | BrowserProvider
): WelephantSwapContract {
  return new Contract(SWAPPER_ADDRESS, WelephantSwapABI as InterfaceAbi, provider) as WelephantSwapContract;
}

/**
 * Typed wrapper for AutoPlayDeposit contract (read-only for frontend)
 */
export interface AutoPlayDepositContract extends Contract {
  // View functions
  balanceOf(player: string): Promise<bigint>;
}

/**
 * Create a WelephantRewards contract instance
 */
export function createWelephantRewardsContract(
  provider: JsonRpcProvider | BrowserProvider
): WelephantRewardsContract {
  return new Contract(WELEPHANT_REWARDS_ADDRESS, WelephantRewardsABI as InterfaceAbi, provider) as WelephantRewardsContract;
}

/**
 * Create an AutoPlayDeposit contract instance (read-only)
 */
export function createAutoPlayDepositContract(
  provider: JsonRpcProvider | BrowserProvider
): AutoPlayDepositContract {
  return new Contract(AUTO_PLAY_DEPOSIT_ADDRESS, AutoPlayDepositABI as InterfaceAbi, provider) as AutoPlayDepositContract;
}

/**
 * Typed wrapper for PayDayInfoList contract (read-only)
 */
export interface PayDayInfoListContract extends Contract {
  getPaydayCount(): Promise<bigint>;
  getPaydays(offset: bigint, limit: bigint): Promise<PaydayInfo[]>;
  getRecentPaydays(count: bigint): Promise<PaydayInfo[]>;
}

/**
 * Create a PayDayInfoList contract instance (read-only)
 */
export function createPayDayInfoListContract(
  provider: JsonRpcProvider | BrowserProvider
): PayDayInfoListContract {
  return new Contract(PAYDAY_INFO_LIST_ADDRESS, PayDayInfoListABI as InterfaceAbi, provider) as PayDayInfoListContract;
}

/**
 * Helper to parse round info tuple to typed object
 */
export function parseRoundInfo(data: [bigint, bigint, bigint, boolean, bigint, boolean, bigint]): RoundInfo {
  return {
    startTime: data[0],
    endTime: data[1],
    totalPot: data[2],
    finalized: data[3],
    winningSquare: data[4],
    hasMinPlayers: data[5],
    participantCount: data[6],
  };
}

/**
 * Helper to parse square info tuple to typed object
 */
export function parseSquareInfo(data: [bigint]): SquareInfo {
  return {
    total: data[0],
    playerCount: 0,
  };
}

/**
 * Helper to parse player balances tuple to typed object
 */
export function parsePlayerBalances(data: [bigint, bigint]): PlayerBalances {
  return {
    ethBalance: data[0],
    welephantBalance: data[1],
  };
}

/**
 * Helper to parse player stats tuple to typed object
 */
export function parsePlayerStats(data: [bigint, bigint, bigint, bigint, bigint]): PlayerStats {
  return {
    ethSpent: data[0],
    roundsParticipated: data[1],
    roundsWon: data[2],
    ethWon: data[3],
    welephantWon: data[4],
  };
}

/**
 * Helper to parse auto-play settings tuple to typed object
 */
export function parseAutoPlaySettings(data: [boolean, bigint, boolean, boolean]): PlayerAutoPlaySettings {
  return {
    useManualSquares: data[0],
    squaresBitmap: data[1],
    loopEthRewards: data[2],
    autoStakeWelephant: data[3],
  };
}

/**
 * Decode bitmap to array of square numbers (1-based: 1 to maxSquares)
 * Bit N-1 represents square N (e.g., bit 0 = square 1, bit 1 = square 2, etc.)
 */
export function decodeBitmap(bitmap: bigint, maxSquares: number): number[] {
  const squares: number[] = [];
  for (let i = 0; i < maxSquares; i++) {
    if ((bitmap & (BigInt(1) << BigInt(i))) !== BigInt(0)) {
      squares.push(i + 1); // 1-based: bit i represents square i+1
    }
  }
  return squares;
}

/**
 * Helper to parse round verification tuple to typed object
 */
export function parseRoundVerification(data: [
  string, string, string, string, string,  // bytes32 fields (0-4)
  bigint, bigint, bigint, bigint, bigint,  // uint256 fields (5-9): blockTimestamp, prevrandao, finalizationBlock, numSquares, paydayChance
  bigint, bigint, bigint,                  // uint256 fields (10-12): singleWinnerChance, computedWinningSquare, storedWinningSquare
  boolean, boolean, boolean, boolean,      // payday/singleWinner flags (13-16)
  boolean                                  // isValid (17)
]): RoundVerification {
  return {
    accumulatedSeed: data[0],
    finalRandomSeed: data[1],
    blockhash1: data[2],
    blockhash2: data[3],
    blockhash3: data[4],
    blockTimestamp: data[5],
    prevrandao: data[6],
    finalizationBlock: data[7],
    numSquares: data[8],
    paydayChance: data[9],
    singleWinnerChance: data[10],
    computedWinningSquare: data[11],
    storedWinningSquare: data[12],
    computedIsPayday: data[13],
    storedIsPayday: data[14],
    computedIsSingleWinner: data[15],
    storedIsSingleWinner: data[16],
    isValid: data[17],
  };
}

/**
 * Helper to parse player detailed stats tuple to typed object
 */
export function parsePlayerDetailedStats(data: [bigint, bigint, bigint, bigint, bigint]): PlayerDetailedStats {
  return {
    totalRounds: data[0] ?? 0n,
    totalPlay: data[1] ?? 0n,
    totalWon: data[2] ?? 0n,
    totalWelephantWon: data[3] ?? 0n,
    averagePlay: data[4] ?? 0n,
  };
}
